import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, HostListener, OnDestroy } from "@angular/core";
import { SlickUtilsService } from "../utils/slick-utils.service";
import { ISlickFileModel, SlickFileModel } from "../slick-file-model/slick-file.model";

const imgExtensions: string[] = ['bmp', 'gif', 'jpeg', 'jpg', 'png', 'tif', 'tiff']

@Component({
	selector: 'slick-file-drop',
	templateUrl: 'slick-file-drop.component.html'
})
export class SlickFileDropComponent implements OnDestroy {
	@Input("displayText") displayText: string = "Drop File or click to Upload";
	@Input() allowVideos: boolean = true;
	@Output("filesChanged") filesChangedEmitter: EventEmitter<ISlickFileModel[]> = new EventEmitter();
	@ViewChild("containerRef") containerRef: ElementRef;
	@ViewChild("fileInput") fileInput: ElementRef;

	private fnOnFileInputChange = (e) => this.onFileInputChange(e);
	private processFileTimeout: NodeJS.Timer;


	@HostListener('click', ['$event'])
	onClick(e) {
		this.fileInput.nativeElement.click();
		(<HTMLInputElement>this.fileInput.nativeElement).removeEventListener("change", this.fnOnFileInputChange, true);
		(<HTMLInputElement>this.fileInput.nativeElement).addEventListener("change", this.fnOnFileInputChange, true);
	}

	ngOnDestroy() {
		(<HTMLInputElement>this.fileInput.nativeElement).removeEventListener("change", this.fnOnFileInputChange, true);
	}

	private onFileInputChange(e) {
		try {
			this.processFileTimeout = setTimeout(() => {
				alert("Processing files timed out");
			}, 10000);

			this.processFiles(Array.from((<HTMLInputElement>this.fileInput.nativeElement).files));

			// A validation to make sure the files all got processed
			clearTimeout(this.processFileTimeout);
			(<HTMLInputElement>this.fileInput.nativeElement).value = '';
		}
		catch (err) {
			alert("SlickFileDrop Error: " + err);
		}
	}

	@HostListener('drop', ['$event'])
	onDrop(e) {
		e.preventDefault();
		e.stopPropagation();

		this.containerRef.nativeElement.classList.remove("slick-file-drop_drag-enter");

		this.processFiles(this.getFilesFromDataTransfer(<DataTransfer>e.dataTransfer));
	}

	// Pull files out of a drop. Normal OS-file drops populate dataTransfer.files, but some
	// sources (e.g. dragging straight out of the Outlook desktop client) deliver the file
	// only through dataTransfer.items — read those synchronously as a fallback.
	private getFilesFromDataTransfer(dataTransfer: DataTransfer): File[] {
		const files: File[] = [];

		if (dataTransfer.files && dataTransfer.files.length > 0) {
			for (let i = 0; i < dataTransfer.files.length; i++)
				files.push(dataTransfer.files.item(i));
		}
		else if (dataTransfer.items && dataTransfer.items.length > 0) {
			for (let i = 0; i < dataTransfer.items.length; i++) {
				const item = dataTransfer.items[i];
				if (item.kind === 'file') {
					const file = item.getAsFile();
					if (file)
						files.push(file);
				}
			}
		}

		// If a drop produced nothing, log what the OS actually handed us. This is the failure case
		// (e.g. dragging from a mail client on some platforms) and tells us exactly why.
		if (files.length === 0) {
			try {
				const types = dataTransfer.types ? Array.from(dataTransfer.types) : [];
				const items = dataTransfer.items
					? Array.from(dataTransfer.items as any).map((i: any) => ({ kind: i.kind, type: i.type }))
					: [];
				console.warn('[slick-file-drop] Drop produced no files.', { types, items, fileCount: dataTransfer.files ? dataTransfer.files.length : 0 });
			}
			catch { }
		}

		return files;
	}

	@HostListener('dragenter', ['$event'])
	onDragEnter(e) {
		// Safari only treats an element as a valid drop target if dragenter is also cancelled
		// (Chrome is happy with just dragover). Without this, drops silently do nothing on Mac.
		e.preventDefault();
		e.stopPropagation();
		this.containerRef.nativeElement.classList.add("slick-file-drop_drag-enter");
	}

	@HostListener('dragover', ['$event'])
	onDragOver(e) {
		e.preventDefault();
		e.stopPropagation();
		// Tell the browser this is a copy so the cursor shows the right affordance (Safari/Mac).
		if (e.dataTransfer)
			e.dataTransfer.dropEffect = 'copy';
	}

	@HostListener('dragleave', ['$event'])
	onDragLeave(e) {
		this.containerRef.nativeElement.classList.remove("slick-file-drop_drag-enter");
	}

	uuid: string;

	constructor() {
		this.uuid = SlickUtilsService.newGuid();
	}

	private async processFiles(files: File[]) {
		let slickFileModels: ISlickFileModel[] = [];
		let imageLoadPromises = [];

		let imgPromises: Promise<string>[] = [];
		for (let i = 0; i < files.length; i++) {
			imageLoadPromises.push(new Promise(async (resolve) => {
				let file: File = files[i];
				let slickFileModel = new SlickFileModel();
				slickFileModel.uuid = SlickUtilsService.newGuid();
				slickFileModel.file = file;
				slickFileModel.name = file.name;
				slickFileModel.title = file.name;

				let fileNameParts = slickFileModel.name.split('.');
				let ext = fileNameParts[fileNameParts.length - 1].toLowerCase();

				if (imgExtensions.indexOf(ext) >= 0) {
					let img = new Image;
					img.onload = () => {	
						slickFileModel.thumbnailBase64Image = this.resizeImageToBase64(img, 90, 90);
						slickFileModel.base64Image = this.resizeImageToBase64(img, 1600, 1600);
						slickFileModels.push(slickFileModel);
						resolve(null);
					}
					img.src = await this.getBase64(file);
				}
				else {
					slickFileModels.push(slickFileModel);
					resolve(null);
				}
			}));
		}

		await Promise.all(imageLoadPromises);
		this.filesChangedEmitter.emit(slickFileModels);
	}

	private resizeImageToBase64(img: HTMLImageElement, width: number, height: number): string {
		let scale: number = 1;
		if (img.width > img.height) {
			if (img.width > width)
				scale = width / img.width;
		}
		else {
			if (img.height > height)
				scale = height / img.height;
		}

		let newWidth = img.width * scale;
		let newHeight = img.height * scale;

		// We create a canvas and get its context.
		var canvas = document.createElement('canvas');
		var ctx = canvas.getContext('2d');

		// We set the dimensions at the wanted size.
		canvas.width = newWidth;
		canvas.height = newHeight;

		// We resize the image with the canvas method drawImage();
		ctx.drawImage(img, 0, 0, newWidth, newHeight);

		return canvas.toDataURL("image/jpeg");
	}

	private getBase64(file: File): Promise<string> {
		return new Promise<string>((resolve, reject) => {
			// Capacitor fails on the FileReader, so we need to get _readReader
			// See: https://github.com/ionic-team/ionic-native/issues/505#issuecomment-503316333
			let fileReader = new FileReader();

			// Get the original real FileReader. The polyfill saves a reference to it.
			const realFileReader = (fileReader as any)._realReader;

			// Make sure we were able to get the original FileReader 
			if (realFileReader) {
				// Swap out the polyfill instance for the original instance.
				fileReader = realFileReader;
			}

			fileReader.readAsDataURL(file);
			fileReader.onload = () => resolve(<string>fileReader.result);
			fileReader.onerror = (error) => reject('Error: ' + error);
		});
	}
}
